import { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import GameSystemPickerModal from "./GameSystemPickerModal";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import GameSystemPickerList from "./GameSystemPickerList";
import { wordStartsWithVowel } from "../UtilityFunctions";
import { hasCookiePermission } from "../../layout/cookies/CookieFunctions";

const IS_GW =
  getEnvironmentVariable("IS_GW") === "true" ||
  getEnvironmentVariable("COMPANY_FLAG") === "gw";

const useStyles = makeStyles((theme) => ({
  category: {
    color: theme?.palette?.secondary?.main,
    fontWeight: "bold",
    opacity: "1 !important",
  },
  selectFlex: {
    "& .MuiSelect-select": {
      display: "flex",
    },
  },
}));

export default function GameSystemPicker({
  name,
  value,
  valueType = "code",
  handleChange,
  gameSystems,
  touched,
  error,
  allowAny,
  required,
  mode,
  request,
}) {
  const classes = useStyles();
  const [items, setItems] = useState([]);

  useEffect(() => {
    const check = async () => {
      var itemArray = [];
      var manufacturer = undefined;
      if (gameSystems?.length > 0) {
        gameSystems.sort((a, b) => {
          if (a.manufacturerOrderNum === b.manufacturerOrderNum) {
            if (a.manufacturer !== b.manufacturer) {
              var manufacturerA = a.manufacturer.toLowerCase();
              var manufacturerB = b.manufacturer.toLowerCase();
              if (manufacturerA > manufacturerB) return 1;
              if (manufacturerA < manufacturerB) return -1;
            }
            return a.gameSystemOrderNum - b.gameSystemOrderNum;
          }
          return a.manufacturerOrderNum - b.manufacturerOrderNum;
        });

        if (allowAny) {
          if (mode === "list") {
            itemArray.push({
              id: "",
              code: "",
              manufacturer: "",
              name: "Any",
            });
          } else {
            itemArray.push(
              <MenuItem value="">
                <em>Any</em>
              </MenuItem>
            );
          }
        }

        let tempGames = [];

        gameSystems.forEach((gameSystem) => {
          if (manufacturer !== gameSystem.manufacturer) {
            manufacturer = gameSystem.manufacturer;
            if (!getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
              if (mode === "list") {
                // Adds basic data of each gs to their respective manufacturer. Used for searching in list component
                if (tempGames?.length) {
                  itemArray.forEach((item, i) => {
                    if (item?.isHeader && item?.code === tempGames?.[0]?.code) {
                      itemArray[i] = {
                        ...itemArray[i],
                        gameSystems: [...tempGames],
                      };
                    }
                  });

                  tempGames = [];
                }

                itemArray.push({
                  isHeader: true,
                  manufacturer: gameSystem.manufacturer,
                  code: gameSystem.code,
                  id: gameSystem.id,
                });
              } else {
                itemArray.push(
                  <MenuItem
                    disabled
                    key={`manufacturerOrderNum:${gameSystem.manufacturerOrderNum}`}
                    value=""
                    className={classes.category}
                  >
                    {gameSystem.manufacturer}
                  </MenuItem>
                );
              }
            }
          }
          let disabled = false;
          if (mode === "list") {
            let hover = disabled ? (
              <Typography>
                To create {wordStartsWithVowel(gameSystem?.name) ? "an" : "a"}{" "}
                {gameSystem?.name} event you require a MyWarhammer account
                linked to your BCP account. To do so, please go to the{" "}
                <Link href="/account?active_tab=identities">Identities</Link>{" "}
                tab on your BCP account and press the Link with MyWarhammer
                button.
              </Typography>
            ) : undefined;

            tempGames.push({
              name: gameSystem?.name,
              manufacturer: gameSystem.manufacturer,
              code: gameSystem.code,
              id: gameSystem.id,
              disabled,
              hover,
            });
            itemArray.push({
              ...gameSystem,
              disabled,
              hover,
            });
          } else {
            itemArray.push(
              <MenuItem
                key={valueType === "id" ? gameSystem.id : gameSystem.code}
                value={valueType === "id" ? gameSystem.id : gameSystem.code}
                disabled={disabled}
              >
                {gameSystem.name}
              </MenuItem>
            );
          }
        });
      }

      setItems(itemArray);
    };

    if (
      mode === "modal" ||
      getEnvironmentVariable("GAMESYSTEM_MODAL") === "true"
    ) {
      return;
    } else {
      check();
    }
    // eslint-disable-next-line
  }, [gameSystems]);

  return (
    <>
      {(getEnvironmentVariable("GAMESYSTEM_MODAL") === "true" &&
        mode !== "list") ||
      mode === "modal" ? (
        <>
          <Typography variant="h4" sx={{ mb: 3 }}>
            Please Select a{" "}
            {getEnvironmentVariable("LABEL_GAMESYSTEM") || "Game System"}
          </Typography>

          <GameSystemPickerModal
            name="gameType"
            gameSystems={gameSystems}
            handleChange={handleChange}
            valueType={valueType}
          />
        </>
      ) : mode === "list" ? (
        <GameSystemPickerList
          items={items}
          handleChange={handleChange}
          request={request}
          valueType={valueType}
        />
      ) : (
        <>
          <Typography variant="h4" sx={{ mb: 3 }}>
            Please Select a{" "}
            {getEnvironmentVariable("LABEL_GAMESYSTEM") || "Game System"}
          </Typography>
          <FormControl
            fullWidth
            variant="outlined"
            className={classes.formControl}
            required={required}
            sx={{
              mt: 2,
              mb: 1,
              color: IS_GW ? "primary.contrastText" : null,
            }}
          >
            <InputLabel
              id={`${name}-label`}
              sx={{
                color: IS_GW ? "primary.contrastText" : null,
                "&.Mui-focused": {
                  color: IS_GW ? "primary.contrastText" : null,
                },
              }}
            >
              {getEnvironmentVariable("LABEL_GAMESYSTEM") || "Game"}
            </InputLabel>
            <Select
              error={Boolean(touched && error)}
              fullWidth
              helperText={touched && error}
              labelId={`${name}-label`}
              name={name}
              value={value}
              onChange={handleChange}
              label={getEnvironmentVariable("LABEL_GAMESYSTEM") || "Game"}
              variant="outlined"
              inputProps={{
                "data-dd-privacy": hasCookiePermission("dataDog")
                  ? "allow"
                  : "mask",
              }}
              sx={{
                backgroundColor: IS_GW ? "primary.main" : null,
              }}
            >
              {items &&
                items.map((gameSystem) => {
                  return gameSystem;
                })}
            </Select>
          </FormControl>
        </>
      )}
    </>
  );
}
